import * as React from 'react';
import { AppBar as RaAppBar, TitlePortal, HideOnScroll, useStore, useTranslate } from 'react-admin';
import { Link, useLocation } from 'react-router-dom';
import { HiOutlineWrenchScrewdriver } from 'react-icons/hi2';
import { ToolbarUserMenu } from './ToolbarUserMenu';
import {
    AppBar as MuiAppBar,
    Toolbar,
    Box,
    IconButton,
    Tooltip,
    Typography,
    useMediaQuery,
    type Theme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import Logo from './Logo';
import { AppBarToolbar } from './AppBarToolbar';
import { NotificationBell } from './NotificationBell';
import { OdooAppSwitcher } from './OdooAppSwitcher';
import { OdooTopNav } from './OdooTopNav';
import { OdooShellMobileNav } from './OdooShellMobileNav';
import { ToolbarCompanyName } from './ToolbarCompanyName';
import type { ThemeName } from '../themes/themes';
import { isOdooShellTheme } from '../apps/activeAppStore';
import { useResolvedActiveAppId } from '../apps/useResolvedActiveAppId';
import {
    ACCOUNTING_SUITE_APP_ID,
    APPS_REGISTRY,
    AUTO_DEALERS_APP_ID,
    AUTO_WORKSHOP_APP_ID,
    INVENTORY_APP_ID,
    FUELING_STATIONS_APP_ID,
    PRA_WORKSPACE_APP_ID,
    PAYROLL_APP_ID,
    PROJECT_MANAGEMENT_APP_ID,
    SALES_APP_ID,
    SETTINGS_APP_ID,
    workspaceRootPath,
} from '../apps/appsRegistry';
import { SETTINGS_RECORD_RULE_FIELD_SETTINGS_PATH } from '../apps/workspacePaths';
import { useCanAccess, useCanAccessAnyInNavSection } from '../auth/useCanAccess';
import { pathInWorkspace } from './odooNavUtils';

const CustomAppBar = () => {
    const isLargeEnough = useMediaQuery<Theme>(theme => theme.breakpoints.up('sm'));
    const [themeName, setThemeName] = useStore<ThemeName>('themeName', 'nano');
    React.useEffect(() => {
        if (themeName !== 'nano') setThemeName('nano');
    }, [themeName, setThemeName]);
    const odoo = isOdooShellTheme(themeName);
    const activeAppId = useResolvedActiveAppId();
    const location = useLocation();
    const canRecordRuleFieldSetup = useCanAccess(SETTINGS_APP_ID, 'securityGroups', 'write');
    const canAutoDealerSales = useCanAccessAnyInNavSection(AUTO_DEALERS_APP_ID, 'sales');
    const translate = useTranslate();
    const adRoot = workspaceRootPath(AUTO_DEALERS_APP_ID);
    /** When set, company GL page "Back" can return to this invoice. */
    const vehicleSaleEditorId = React.useMemo(() => {
        const m = location.pathname.match(/\/vehicleSaleInfo\/(\d+)/);
        if (!m) return null;
        const id = Number(m[1]);
        return Number.isFinite(id) && id > 0 ? id : null;
    }, [location.pathname]);
    const inAccountingWorkspace = activeAppId === ACCOUNTING_SUITE_APP_ID;
    const hideTitlePortal =
        inAccountingWorkspace ||
        activeAppId === SETTINGS_APP_ID ||
        activeAppId === AUTO_DEALERS_APP_ID ||
        activeAppId === AUTO_WORKSHOP_APP_ID ||
        activeAppId === INVENTORY_APP_ID ||
        activeAppId === FUELING_STATIONS_APP_ID ||
        activeAppId === 'purchases' ||
        activeAppId === SALES_APP_ID ||
        activeAppId === PROJECT_MANAGEMENT_APP_ID ||
        // Payroll follows the Auto Workshop module bar: the open document's title never
        // takes a slot in the nav row (the record header already shows it).
        activeAppId === PAYROLL_APP_ID;
    const workspaceAppLabel = React.useMemo(() => {
        const entry = APPS_REGISTRY.find(a => a.id === activeAppId);
        return entry?.name ?? null;
    }, [activeAppId]);

    if (!odoo) {
        return (
            <RaAppBar
                color="secondary"
                toolbar={
                    <>
                        <AppBarToolbar />
                        {/* Last in the toolbar slot, so it lands immediately before react-admin's
                            own UserMenu — same "bell sits with the identity controls" placement as
                            the Odoo shell below. */}
                        <NotificationBell />
                    </>
                }
            >
                <TitlePortal />
                {isLargeEnough && <Logo />}
                {isLargeEnough && <Box component="span" sx={{ flex: 1 }} />}
            </RaAppBar>
        );
    }

    return (
        <HideOnScroll>
            <MuiAppBar
                position="sticky"
                color="secondary"
                elevation={0}
                sx={{
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    px: { xs: 0.5, sm: 1 },
                    pt: 0,
                    pb: 0,
                    mb: 0,
                }}
            >
                <Toolbar
                    disableGutters
                    variant="dense"
                    sx={theme => ({
                        px: 1,
                        minHeight: 48,
                        minWidth: 0,
                        gap: 0.5,
                        // From `md` up the module bar shrinks into its own overflow menu instead
                        // of wrapping the toolbar onto a second row.
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        alignItems: 'center',
                        // PRA workspace carries its own maroon identity (matches the PRAWelcome banner);
                        // every other workspace keeps the shared theme bar.
                        ...(activeAppId === PRA_WORKSPACE_APP_ID
                            ? {
                                  background:
                                      'linear-gradient(90deg, #5f1111 0%, #8E1B1B 55%, #b83a3a 100%)',
                                  color: '#fff',
                              }
                            : {
                                  bgcolor: (theme.vars || theme).palette.secondary.main,
                              }),
                        borderRadius: 1.25,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                        border: '1px solid rgba(0,0,0,0.08)',
                    })}
                >
                    <OdooShellMobileNav />
                    <OdooAppSwitcher />
                    {workspaceAppLabel ? (
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{
                                flexShrink: 0,
                                mr: 0.75,
                                pl: 0.25,
                                fontSize: '0.9375rem',
                                fontWeight: 600,
                                lineHeight: 1.2,
                                letterSpacing: '0.01em',
                                color: '#FFFFF0',
                                display: { xs: 'none', sm: 'inline' },
                            }}
                        >
                            {workspaceAppLabel}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            display: hideTitlePortal ? 'none' : 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            flexShrink: 0,
                            maxWidth: { xs: '40%', sm: 220 },
                        }}
                    >
                        <TitlePortal />
                    </Box>
                    <OdooTopNav />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                            marginInlineStart: 'auto',
                        }}
                    >
                        {activeAppId === SETTINGS_APP_ID && canRecordRuleFieldSetup ? (
                            <Tooltip title="Record rule fields (developer)">
                                <IconButton
                                    component={Link}
                                    to={SETTINGS_RECORD_RULE_FIELD_SETTINGS_PATH}
                                    size="small"
                                    color="inherit"
                                    aria-label="Record rule fields"
                                >
                                    <HiOutlineWrenchScrewdriver size={22} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {activeAppId === AUTO_DEALERS_APP_ID && canAutoDealerSales ? (
                            <Tooltip
                                title={translate('shell.auto_dealers.journal_gl_topnav', {
                                    _: 'Vehicle sale journal GL accounts (company-wide — all invoices)',
                                })}
                            >
                                <IconButton
                                    component={Link}
                                    to={pathInWorkspace(adRoot, '/vehicleSaleGlCompanyDefaults')}
                                    state={
                                        vehicleSaleEditorId
                                            ? { returnTo: `${location.pathname}${location.search}` }
                                            : undefined
                                    }
                                    size="small"
                                    color="inherit"
                                    aria-label={translate('shell.auto_dealers.journal_gl_topnav', {
                                        _: 'Vehicle sale journal GL accounts (company-wide)',
                                    })}
                                >
                                    <HiOutlineWrenchScrewdriver size={22} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        <AppBarToolbar />
                        <ToolbarCompanyName />
                        <NotificationBell />
                        <ToolbarUserMenu />
                    </Box>
                </Toolbar>
            </MuiAppBar>
        </HideOnScroll>
    );
};

export default CustomAppBar;
